/**
 * "Insights" report body — the SECOND Reports-tab report, alongside the Standard report (ReportBody in
 * ReportsTab.tsx, which this file never imports from or modifies). Three sections, each grounded in a
 * table/column the Standard report does not already show, and each deliberately avoiding
 * duration_minutes_total / "meeting minutes" (corrupted for dates >= 2026-06-10 — see
 * app/modules/reports/service.py's module docstring on the backend):
 *
 *   1. License seat utilization  — sub_account_plan_usage's own snapshot TIME SERIES (the Usage tab
 *      only ever shows the latest snapshot; this is genuinely new, not a re-skin).
 *   2. Meeting/webinar size distribution — participant_count histogram (a headcount, not a duration).
 *   3. Weekly activity rhythm — weekday/hour-of-day counts (Asia/Bangkok), never shown elsewhere.
 *
 * No phone section: the older Insights phone trend was removed (owner ruling 2026-09-19) — the Standard
 * report's Zoom Phone section is the only phone view.
 *
 * Uses the SAME shared atoms as the rest of Customer-360 (KpiTile/Section/FeedNote/UtilizationBar/
 * utilizationTone/bandFromPct from ../display, the shared tooltip from ../chartTooltip) so it reads as
 * part of one system rather than a bolted-on second app.
 */

import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { EmptyState } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatDate, formatInt } from "@/lib/format";
import { planName } from "@/lib/planNames";
import type {
  CustomerReportInsights,
  LicenseTrendBucket,
  ReportType,
  WeeklyPattern,
} from "@/api/types";
import {
  KpiTile,
  Section,
  FeedNote,
  UtilizationBar,
  utilizationTone,
  bandFromPct,
  weekdayShort,
  type UtilBand,
} from "../display";
import { TARGET_FOCUS, useChartTooltip } from "../chartTooltip";

/** Band -> the same solid fill classes UtilizationBar uses, so a bar's colour and a meter's colour for
 *  the SAME band always match. Not a new colour meaning — just applying the existing one to a bar. */
const BAND_FILL: Record<UtilBand, string> = {
  high: "bg-(--warn)",
  healthy: "bg-(--ok)",
  low: "bg-(--info)",
  very_low: "bg-(--danger)",
  unknown: "bg-(--text-subtle)",
};

export function InsightsReportBody({
  data,
  type,
}: {
  data: CustomerReportInsights;
  type: ReportType;
}) {
  const { t } = useTranslation();
  const { meta, license_utilization, size_distribution, weekly_pattern } = data;

  const eventsLabel =
    type === "webinars"
      ? t("customer.reports.kpiWebinars")
      : type === "meetings"
        ? t("customer.reports.kpiMeetings")
        : t("customer.reports.kpiEvents");

  return (
    <div className="flex flex-col gap-6">
      <LicenseUtilizationSection lic={license_utilization} t={t} />
      <SizeDistributionSection
        dist={size_distribution}
        unit={eventsLabel}
        coveragePct={meta.history_event_coverage_pct}
        t={t}
      />
      <WeeklyPatternSection pattern={weekly_pattern} unit={eventsLabel} t={t} />
    </div>
  );
}

function LicenseUtilizationSection({
  lic,
  t,
}: {
  lic: CustomerReportInsights["license_utilization"];
  t: TFunction;
}) {
  const primary = lic.latest_by_plan.find((r) => r.plan_type === lic.primary_plan_type) ?? null;
  const band = bandFromPct(primary?.utilization_pct);
  const otherPlanCount = lic.latest_by_plan.length - (primary ? 1 : 0);

  return (
    <Section title={t("customer.reportsInsights.licenseTitle")}>
      {lic.primary_plan_type == null ? (
        <EmptyState title={t("customer.reportsInsights.licenseEmpty")} />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
            <KpiTile
              label={t("customer.reportsInsights.licenseKpiCurrent")}
              value={
                <UtilizationBar
                  pct={primary?.utilization_pct}
                  tone={utilizationTone(band)}
                  className="mt-1"
                />
              }
              sub={
                <>
                  {t(`utilizationBand.${band}`)}
                  {primary?.snapshot_at &&
                    ` · ${t("customer.reportsInsights.licenseKpiAsOf", { date: formatDate(primary.snapshot_at) })}`}
                </>
              }
            />
          </div>
          <MiniBarChart
            caption={t("customer.reportsInsights.licenseTrendTitle", {
              plan: planName(lic.primary_plan_type),
            })}
            bars={lic.trend.map((b: LicenseTrendBucket) => ({
              label: b.bucket,
              value: b.utilization_pct ?? 0,
              fillClass: BAND_FILL[bandFromPct(b.utilization_pct)],
              display: b.utilization_pct == null ? "—" : `${b.utilization_pct}%`,
            }))}
          />
          {otherPlanCount > 0 && (
            <FeedNote>
              {t("customer.reportsInsights.licenseOtherPlansNote", { count: otherPlanCount })}
            </FeedNote>
          )}
        </div>
      )}
    </Section>
  );
}

function SizeDistributionSection({
  dist,
  unit,
  coveragePct,
  t,
}: {
  dist: CustomerReportInsights["size_distribution"];
  unit: string;
  coveragePct: number | null;
  t: TFunction;
}) {
  if (dist.total_events === 0) {
    return (
      <Section title={t("customer.reportsInsights.sizeTitle", { unit })}>
        <EmptyState title={t("customer.reportsInsights.sizeEmpty")} />
      </Section>
    );
  }
  const top = dist.buckets.reduce((best, b) => (b.count > best.count ? b : best), {
    label: "",
    count: -1,
    participants: 0,
  });

  return (
    <Section title={t("customer.reportsInsights.sizeTitle", { unit })}>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
          <KpiTile
            label={t("customer.reportsInsights.sizeKpiAvg")}
            value={dist.avg_participants == null ? "—" : String(dist.avg_participants)}
          />
          <KpiTile
            label={t("customer.reportsInsights.sizeKpiTop")}
            value={top.count > 0 ? top.label : "—"}
          />
        </div>
        <MiniBarChart
          caption={t("customer.reportsInsights.sizeCaption", { unit })}
          bars={dist.buckets.map((b) => ({
            label: b.label,
            value: b.count,
            fillClass: "bg-(--brand)",
          }))}
        />
        {coveragePct != null && coveragePct < 99 && (
          <FeedNote>{t("customer.reportsInsights.coverageNote", { pct: coveragePct })}</FeedNote>
        )}
      </div>
    </Section>
  );
}

function WeeklyPatternSection({
  pattern,
  unit,
  t,
}: {
  pattern: WeeklyPattern;
  unit: string;
  t: TFunction;
}) {
  const total =
    pattern.by_weekday.reduce((sum, d) => sum + d.count, 0) +
    pattern.by_hour.reduce((sum, h) => sum + h.count, 0);
  if (total === 0) {
    return (
      <Section title={t("customer.reportsInsights.weeklyTitle")}>
        <EmptyState title={t("customer.reportsInsights.weeklyEmpty")} />
      </Section>
    );
  }
  return (
    <Section title={t("customer.reportsInsights.weeklyTitle")}>
      <div className="flex flex-col gap-3">
        <MiniBarChart
          caption={t("customer.reportsInsights.weeklyByWeekdayCaption", { unit })}
          bars={pattern.by_weekday.map((d) => ({
            label: weekdayShort(t, d.weekday),
            value: d.count,
            fillClass: "bg-(--data-neutral-solid)",
          }))}
        />
        <MiniBarChart
          caption={t("customer.reportsInsights.weeklyByHourCaption", { unit })}
          bars={pattern.by_hour.map((h) => ({
            label: String(h.hour),
            value: h.count,
            fillClass: "bg-(--data-violet-solid)",
          }))}
        />
      </div>
    </Section>
  );
}

/**
 * Single-series HTML bar chart (mirrors ReportsTab's own BarChart visual language: value above the bar,
 * dense-range label thinning past 18 bars, an sr-only data table) — kept local here rather than imported
 * from ReportsTab.tsx (which owns the Standard report's untouched code) or exported from ../charts
 * (whose ChartTone union has no danger/neutral fill, needed for the per-band-coloured license trend). A
 * per-bar `fillClass` lets each bar carry its own Tailwind fill, e.g. a different utilization-band
 * colour per bucket.
 *
 * Tooltips: the SAME mechanism as ../charts (../chartTooltip `useChartTooltip` — instant, per bar, keyboard via one
 * roving tab stop, clamped to the figure, hidden in print), not a native `title` (review 2026-09-19:
 * the owner's "slow" complaint would otherwise still show on this tab).
 */
function MiniBarChart({
  bars,
  caption,
}: {
  bars: { label: string; value: number; fillClass: string; display?: string }[];
  caption: string;
}) {
  const max = Math.max(1, ...bars.map((b) => b.value));
  const dense = bars.length > 18;
  const { figureRef, target, bubble } = useChartTooltip();

  return (
    <figure
      ref={figureRef}
      className="relative flex flex-col gap-2 rounded-(--radius) border border-(--border) bg-(--surface) p-4 shadow-(--shadow-sm)"
    >
      <figcaption className="text-(length:--text-xs) font-medium text-(--text-muted)">
        {caption}
      </figcaption>
      <div className="flex flex-col gap-1">
        {dense && (
          <div className="flex items-center gap-2">
            <span className="tabular text-(length:--text-2xs) font-medium leading-none text-(--text-muted)">
              {formatInt(max)}
            </span>
            <span className="h-px flex-1 bg-(--border)" aria-hidden />
          </div>
        )}
        <div className="flex h-48 items-end gap-1.5">
          {bars.map((b, i) => (
            <div
              key={`${b.label}:${i}`}
              className={cn(
                "flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1",
                TARGET_FOCUS,
              )}
              {...target(
                `m${i}`,
                { period: b.label, series: caption, value: b.display ?? formatInt(b.value) },
                { col: i },
              )}
            >
              {!dense && (
                <span className="tabular text-(length:--text-2xs) font-semibold text-(--text-muted)">
                  {b.display ?? formatInt(b.value)}
                </span>
              )}
              <div
                className={`w-full rounded-t-(--radius-sm) ${b.fillClass} transition-[height] duration-(--dur) ease-(--ease-out)`}
                style={{ height: `${Math.max(1.5, (b.value / max) * 100)}%` }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-1.5">
        {bars.map((b, i) => (
          <span
            key={`${b.label}:x:${i}`}
            className="tabular min-w-0 flex-1 truncate text-center text-(length:--text-2xs) text-(--text-muted)"
          >
            {dense ? (i === 0 || i === bars.length - 1 ? b.label : "") : b.label}
          </span>
        ))}
      </div>
      <table className="sr-only">
        <caption>{caption}</caption>
        <tbody>
          {bars.map((b, i) => (
            <tr key={`${b.label}:sr:${i}`}>
              <td>{b.label}</td>
              <td>{b.display ?? b.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {bubble}
    </figure>
  );
}
